import { useState, useEffect, useRef, useMemo } from 'react';
import { useMarketContext } from '../context/MarketDataContext';
import { getValidToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || '';

// ---------------------------------------------------------------------------
// Enhanced S&P 500 Market - every widget here is WebSocket-driven.
//
// Data sources (all already streaming through MarketDataContext):
//   spxLive         real SPX via put-call parity on 0DTE SPXW quotes (~2s)
//   spxFlow         0DTE call/put chain volume+premium (30s, with deltas)
//   spxZones        ChartPrime high-volume S/R boxes on SPY 1-min, SPX pts (60s)
//   underlyingTicks SPY trade tape (250ms throttle)
//   tickers         1-min AM bars for all 503 components
//
// Design note: unlike /sp500-market, no widget here repeats another's
// signal - each card is an independent input for judging SPX direction.
// ---------------------------------------------------------------------------

const fmtPts = (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)} pts`;
const fmtPct = (v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;

function Card({ title, badge, badgeColor, children }) {
  return (
    <div className="bg-gray-900 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{title}</span>
        {badge && (
          <span className="text-xs font-bold px-2 py-0.5 rounded font-mono"
            style={{ backgroundColor: (badgeColor || '#6b7280') + '22', color: badgeColor || '#9ca3af' }}>
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

// 1 ─ Live SPX header: parity price + session sparkline ----------------------
function LiveSPXHeader({ spxLive }) {
  const histRef = useRef([]); // [{t, p}] session samples
  const [, force] = useState(0);
  useEffect(() => {
    if (spxLive?.price > 0) {
      const h = histRef.current;
      if (!h.length || h[h.length - 1].p !== spxLive.price) {
        h.push({ t: spxLive.ts, p: spxLive.price });
        if (h.length > 1200) h.splice(0, h.length - 1200); // ~40min at 2s
        force((c) => c + 1);
      }
    }
  }, [spxLive]);

  const h = histRef.current;
  const first = h[0]?.p, last = h[h.length - 1]?.p;
  const chg = first && last ? last - first : 0;
  const color = chg >= 0 ? '#22c55e' : '#ef4444';

  // inline sparkline (SVG, no lib)
  let path = '';
  if (h.length > 1) {
    const min = Math.min(...h.map((x) => x.p)), max = Math.max(...h.map((x) => x.p));
    const span = max - min || 1;
    path = h.map((x, i) =>
      `${i === 0 ? 'M' : 'L'}${(i / (h.length - 1) * 300).toFixed(1)},${(38 - (x.p - min) / span * 34).toFixed(1)}`
    ).join(' ');
  }

  const live = spxLive?.src === 'parity';
  return (
    <div className="bg-gray-900 rounded-2xl p-5 flex items-center gap-6 flex-wrap">
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">S&P 500 — real index (put-call parity)</div>
        <div className="flex items-baseline gap-3">
          <span className="text-3xl font-bold font-mono text-white">
            {spxLive?.price ? spxLive.price.toLocaleString('en-US', { minimumFractionDigits: 2 }) : '—'}
          </span>
          {h.length > 1 && (
            <span className="text-sm font-mono font-semibold" style={{ color }}>
              {fmtPts(chg)} since load
            </span>
          )}
          <span className="text-xs px-2 py-0.5 rounded font-semibold"
            style={{ backgroundColor: (live ? '#22c55e' : '#f59e0b') + '22', color: live ? '#22c55e' : '#f59e0b' }}>
            {live ? '● LIVE (parity)' : spxLive ? '◐ delayed (FMP)' : 'waiting…'}
          </span>
        </div>
      </div>
      {path && (
        <svg width="300" height="40" className="flex-shrink-0">
          <path d={path} fill="none" stroke={color} strokeWidth="1.5" />
        </svg>
      )}
    </div>
  );
}

// 2 ─ 0DTE put/call flow -----------------------------------------------------
function FlowGauge({ spxFlow }) {
  if (!spxFlow) return <Card title="0DTE Options Flow (SPX)"><div className="text-gray-600 text-sm">Waiting for first chain sample…</div></Card>;
  const { call_vol = 0, put_vol = 0, call_prem = 0, put_prem = 0, d_call_vol, d_put_vol } = spxFlow;
  const tot = call_vol + put_vol;
  const callShare = tot > 0 ? call_vol / tot : 0.5;
  const deltaTot = (d_call_vol || 0) + (d_put_vol || 0);
  const recentCallShare = deltaTot > 0 ? (d_call_vol || 0) / deltaTot : null;
  const lean = recentCallShare == null ? null : recentCallShare - 0.5;
  const leanTxt = lean == null ? 'building baseline' :
    Math.abs(lean) < 0.05 ? 'balanced' : lean > 0 ? 'CALL-side pressure' : 'PUT-side pressure';
  const leanColor = lean == null ? '#6b7280' : Math.abs(lean) < 0.05 ? '#9ca3af' : lean > 0 ? '#22c55e' : '#ef4444';
  return (
    <Card title="0DTE Options Flow (SPX)" badge={leanTxt} badgeColor={leanColor}>
      <div className="flex h-3 rounded overflow-hidden mb-2" title="session call vs put volume">
        <div style={{ width: `${callShare * 100}%`, backgroundColor: '#22c55e' }} />
        <div style={{ flex: 1, backgroundColor: '#ef4444' }} />
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs font-mono">
        <div>
          <div className="text-gray-500">CALL vol / premium</div>
          <div className="text-green-400">{call_vol.toLocaleString()} · ${(call_prem / 1e6).toFixed(1)}M</div>
        </div>
        <div className="text-right">
          <div className="text-gray-500">PUT vol / premium</div>
          <div className="text-red-400">{put_vol.toLocaleString()} · ${(put_prem / 1e6).toFixed(1)}M</div>
        </div>
        {d_call_vol != null && (
          <div className="col-span-2 text-gray-400">
            last 30s: <span className="text-green-400">+{(d_call_vol || 0).toLocaleString()} C</span>
            {' / '}<span className="text-red-400">+{(d_put_vol || 0).toLocaleString()} P</span>
          </div>
        )}
      </div>
    </Card>
  );
}

// 3 ─ Tape pressure (SPY upticks vs downticks) --------------------------------
function TapePressure({ spyTick }) {
  // Keep our own tick history: {t, dir} for each price change.
  const histRef = useRef([]);
  const lastRef = useRef(null);
  const [, force] = useState(0);
  useEffect(() => {
    if (spyTick == null) return;
    const prev = lastRef.current;
    lastRef.current = spyTick;
    if (prev == null || spyTick === prev) return;
    const h = histRef.current;
    h.push({ t: Date.now(), dir: spyTick > prev ? 1 : -1 });
    const cutoff = Date.now() - 5 * 60 * 1000;
    while (h.length && h[0].t < cutoff) h.shift();
    force((c) => c + 1);
  }, [spyTick]);

  const h = histRef.current;
  const now = Date.now();
  const win = (ms) => {
    const xs = h.filter((x) => x.t >= now - ms);
    const up = xs.filter((x) => x.dir > 0).length;
    return { up, dn: xs.length - up, n: xs.length };
  };
  const m1 = win(60e3), m5 = win(300e3);
  const ratio = (w) => (w.n === 0 ? 0 : (w.up - w.dn) / w.n);
  const col = (r) => (r > 0.1 ? '#22c55e' : r < -0.1 ? '#ef4444' : '#9ca3af');
  return (
    <Card title="Tape Pressure (SPY up/downticks)"
      badge={m1.n ? `${(ratio(m1) * 100).toFixed(0)}% 1m` : 'no ticks yet'}
      badgeColor={col(ratio(m1))}>
      {[['1 min', m1], ['5 min', m5]].map(([label, w]) => (
        <div key={label} className="mb-2">
          <div className="flex justify-between text-xs font-mono mb-1">
            <span className="text-gray-500">{label}</span>
            <span><span className="text-green-400">▲{w.up}</span> <span className="text-red-400">▼{w.dn}</span></span>
          </div>
          <div className="flex h-2 rounded overflow-hidden bg-gray-800">
            {w.n > 0 && <div style={{ width: `${w.up / w.n * 100}%`, backgroundColor: '#22c55e' }} />}
            {w.n > 0 && <div style={{ flex: 1, backgroundColor: '#ef4444' }} />}
          </div>
        </div>
      ))}
      <div className="text-[10px] text-gray-600">tick stream throttled to 250ms server-side; counts are of price changes, not raw prints</div>
    </Card>
  );
}

// 4 ─ VWAP distance -----------------------------------------------------------
function VwapDistance({ tickers, spxLive }) {
  // Session VWAP from SPY's streamed 1-min bars, accumulated client-side.
  const accRef = useRef({ pv: 0, v: 0, lastTs: null, day: null });
  const spy = tickers?.SPY;
  useEffect(() => {
    if (!spy?.timestamp || !(spy.volume > 0) || !(spy.price > 0)) return;
    const a = accRef.current;
    const day = new Date(spy.timestamp).toDateString();
    if (a.day !== day) { a.pv = 0; a.v = 0; a.lastTs = null; a.day = day; }
    if (a.lastTs === spy.timestamp) return; // same 1-min bar re-broadcast
    a.lastTs = spy.timestamp;
    a.pv += spy.price * spy.volume;
    a.v += spy.volume;
  }, [spy]);

  const a = accRef.current;
  const vwapSpy = a.v > 0 ? a.pv / a.v : null;
  const k = spxLive?.price && spy?.price ? spxLive.price / spy.price : null;
  const vwapSpx = vwapSpy && k ? vwapSpy * k : null;
  const dist = vwapSpx && spxLive?.price ? spxLive.price - vwapSpx : null;
  const color = dist == null ? '#6b7280' : dist >= 0 ? '#22c55e' : '#ef4444';
  return (
    <Card title="VWAP Distance (SPX-anchored)"
      badge={dist == null ? 'accumulating…' : dist >= 0 ? 'above VWAP - trend up' : 'below VWAP - trend down'}
      badgeColor={color}>
      {vwapSpx ? (
        <div className="font-mono">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">session VWAP</span>
            <span className="text-gray-200">{vwapSpx.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-gray-500">distance</span>
            <span className="font-semibold" style={{ color }}>{fmtPts(dist)}</span>
          </div>
          <div className="text-[10px] text-gray-600 mt-2">
            VWAP built from SPY 1-min streamed bars since page load (full-session accuracy after a reload mid-day requires the earlier bars; treat early readings as approximate).
          </div>
        </div>
      ) : <div className="text-gray-600 text-sm">Accumulating streamed bars…</div>}
    </Card>
  );
}

// 5 ─ MegaCap vs the rest ------------------------------------------------------
function MegaCapVsRest({ tickers, components }) {
  const calc = useMemo(() => {
    if (!components?.length) return null;
    const top8 = [...components].sort((x, y) => y.weight - x.weight).slice(0, 8);
    const top8Set = new Set(top8.map((c) => c.symbol));
    let wTop = 0, sTop = 0, wRest = 0, sRest = 0;
    for (const c of components) {
      const t = tickers[c.symbol];
      if (!t || !t.mainOpen) continue;
      const chg = (t.price - t.mainOpen) / t.mainOpen * 100;
      if (top8Set.has(c.symbol)) { wTop += c.weight; sTop += chg * c.weight; }
      else { wRest += c.weight; sRest += chg * c.weight; }
    }
    if (wTop === 0 || wRest === 0) return null;
    return { top: sTop / wTop, rest: sRest / wRest, names: top8.map((c) => c.symbol) };
  }, [tickers, components]);

  if (!calc) return <Card title="MegaCap vs Equal-Weight"><div className="text-gray-600 text-sm">Waiting for live component bars…</div></Card>;
  const gap = calc.top - calc.rest;
  const narrow = Math.abs(gap) > 0.25;
  const verdict = !narrow ? 'BROAD move' : gap > 0 ? 'NARROW - megacap-carried' : 'NARROW - rest leads, megacaps lag';
  return (
    <Card title="MegaCap vs Equal-Weight" badge={verdict} badgeColor={narrow ? '#f59e0b' : '#22c55e'}>
      <div className="font-mono text-sm">
        <div className="flex justify-between"><span className="text-gray-500">Top-8 (wtd)</span>
          <span style={{ color: calc.top >= 0 ? '#22c55e' : '#ef4444' }}>{fmtPct(calc.top)}</span></div>
        <div className="flex justify-between mt-1"><span className="text-gray-500">Other 495 (wtd)</span>
          <span style={{ color: calc.rest >= 0 ? '#22c55e' : '#ef4444' }}>{fmtPct(calc.rest)}</span></div>
        <div className="flex justify-between mt-1 pt-1 border-t border-gray-800"><span className="text-gray-500">gap</span>
          <span className="font-semibold" style={{ color: narrow ? '#f59e0b' : '#9ca3af' }}>{fmtPct(gap)}</span></div>
      </div>
      <div className="text-[10px] text-gray-600 mt-2">narrow (megacap-only) moves fade more often than broad ones · top-8: {calc.names.join(' ')}</div>
    </Card>
  );
}

// 6 ─ High-volume S/R zones ----------------------------------------------------
function SRZones({ spxZones, spxLive }) {
  if (!spxZones) return <Card title="High-Volume S/R Zones"><div className="text-gray-600 text-sm">Computing zones (updates every 60s)…</div></Card>;
  const px = spxLive?.price;
  const row = (label, z, color) => z ? (
    <div className="flex justify-between items-baseline font-mono text-sm mb-1">
      <span style={{ color }}>{label}</span>
      <span className="text-gray-200">{z.lo.toFixed(0)} – {z.hi.toFixed(0)}</span>
      <span className="text-gray-500 text-xs">{px ? fmtPts((label === 'resistance' ? z.lo : z.hi) - px) : ''} · {z.at}</span>
    </div>
  ) : <div className="text-gray-600 text-xs mb-1">no live {label} zone</div>;
  return (
    <Card title="High-Volume S/R Zones (ChartPrime port)">
      {row('resistance', spxZones.resistance, '#ef4444')}
      {row('support', spxZones.support, '#22c55e')}
      <div className="text-[10px] text-gray-600 mt-2">
        pivot ±20 bars on SPY 1-min extended session, high-volume filter, ATR(200) width — converted to SPX points. Zones confirm ~20 min after the actual turn (indicator design, not lag here).
      </div>
    </Card>
  );
}

// 7 ─ Risk-on / risk-off --------------------------------------------------------
const OFFENSE = new Set(['Information Technology', 'Consumer Discretionary']);
const DEFENSE = new Set(['Consumer Staples', 'Utilities']);
function RiskOnOff({ tickers, components }) {
  const calc = useMemo(() => {
    if (!components?.length) return null;
    let wo = 0, so = 0, wd = 0, sd = 0;
    for (const c of components) {
      const t = tickers[c.symbol];
      if (!t || !t.mainOpen) continue;
      const chg = (t.price - t.mainOpen) / t.mainOpen * 100;
      if (OFFENSE.has(c.sector)) { wo += c.weight; so += chg * c.weight; }
      else if (DEFENSE.has(c.sector)) { wd += c.weight; sd += chg * c.weight; }
    }
    if (wo === 0 || wd === 0) return null;
    return { off: so / wo, def: sd / wd };
  }, [tickers, components]);

  if (!calc) return <Card title="Risk On / Risk Off"><div className="text-gray-600 text-sm">Waiting for live component bars…</div></Card>;
  const spread = calc.off - calc.def;
  const label = spread > 0.1 ? 'RISK-ON' : spread < -0.1 ? 'RISK-OFF' : 'NEUTRAL';
  const color = spread > 0.1 ? '#22c55e' : spread < -0.1 ? '#ef4444' : '#9ca3af';
  return (
    <Card title="Risk On / Risk Off (sector rotation)" badge={label} badgeColor={color}>
      <div className="font-mono text-sm">
        <div className="flex justify-between"><span className="text-gray-500">Offense (Tech+Disc)</span>
          <span style={{ color: calc.off >= 0 ? '#22c55e' : '#ef4444' }}>{fmtPct(calc.off)}</span></div>
        <div className="flex justify-between mt-1"><span className="text-gray-500">Defense (Stap+Util)</span>
          <span style={{ color: calc.def >= 0 ? '#22c55e' : '#ef4444' }}>{fmtPct(calc.def)}</span></div>
        <div className="flex justify-between mt-1 pt-1 border-t border-gray-800"><span className="text-gray-500">spread</span>
          <span className="font-semibold" style={{ color }}>{fmtPct(spread)}</span></div>
      </div>
    </Card>
  );
}

// 8 ─ SPY/SPX basis divergence ---------------------------------------------------
function BasisDivergence({ spxLive, spyTick }) {
  // Anchor ratio at the first joint sample, then track deviation.
  const anchorRef = useRef(null);
  const histRef = useRef([]);
  const [, force] = useState(0);
  useEffect(() => {
    if (!spxLive?.price || !spyTick || spxLive.src !== 'parity') return;
    if (anchorRef.current == null) anchorRef.current = spxLive.price / spyTick;
    const basis = spxLive.price - spyTick * anchorRef.current;
    const h = histRef.current;
    h.push(basis);
    if (h.length > 600) h.shift();
    force((c) => c + 1);
  }, [spxLive, spyTick]);

  const h = histRef.current;
  const cur = h[h.length - 1];
  const maxAbs = h.length ? Math.max(...h.map(Math.abs)) : 0;
  const unusual = cur != null && Math.abs(cur) > 5;
  return (
    <Card title="SPY→SPX Basis Divergence"
      badge={cur == null ? 'anchoring…' : unusual ? 'DIVERGING' : 'normal'}
      badgeColor={cur == null ? '#6b7280' : unusual ? '#f59e0b' : '#22c55e'}>
      {cur != null ? (
        <div className="font-mono text-sm">
          <div className="flex justify-between"><span className="text-gray-500">current basis</span>
            <span className="font-semibold" style={{ color: unusual ? '#f59e0b' : '#9ca3af' }}>{fmtPts(cur)}</span></div>
          <div className="flex justify-between mt-1"><span className="text-gray-500">session max |basis|</span>
            <span className="text-gray-400">{maxAbs.toFixed(1)} pts</span></div>
          <div className="text-[10px] text-gray-600 mt-2">
            real SPX (parity) minus SPY×(ratio anchored at page load). A growing gap means the index and the ETF are being moved by different flows — the same divergence that put the old SPY-proxy price 20pts off on 07-28.
          </div>
        </div>
      ) : <div className="text-gray-600 text-sm">Waiting for parity + SPY tick…</div>}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function SP500Enhanced() {
  const { tickers, underlyingTicks, spxLive, spxFlow, spxZones, connected } = useMarketContext();
  const [components, setComponents] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = await getValidToken();
        const res = await fetch(`${API_URL}/api/sp500/components`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (mounted) setComponents((data.components || data || []).map((c) => ({
          symbol: c.symbol, weight: Number(c.weight), sector: c.sector,
        })));
      } catch { /* widgets degrade to waiting states */ }
    })();
    return () => { mounted = false; };
  }, []);

  const spyTick = underlyingTicks?.SPY;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Enhanced S&P 500 Market</h1>
          <p className="text-sm text-gray-500 mt-1">
            Independent SPX-direction inputs, all WebSocket-driven {connected ? '· stream connected' : '· stream reconnecting…'}
          </p>
        </div>
      </div>

      <div className="mb-4"><LiveSPXHeader spxLive={spxLive} /></div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <FlowGauge spxFlow={spxFlow} />
        <TapePressure spyTick={spyTick} />
        <VwapDistance tickers={tickers} spxLive={spxLive} />
        <MegaCapVsRest tickers={tickers} components={components} />
        <SRZones spxZones={spxZones} spxLive={spxLive} />
        <RiskOnOff tickers={tickers} components={components} />
        <BasisDivergence spxLive={spxLive} spyTick={spyTick} />
      </div>
    </div>
  );
}
